/**
 * Hangar page state (Phase 6): the catalog view from the API, the selected mech, one loadout
 * draft per mech, and the calls that save, buy and reload. Every price and every refusal comes
 * from the server; the store only asks and shows.
 */
import { getMech, MECHS, type MechDefinition } from '@steelwake/shared/data';
import type { Loadout } from '@steelwake/shared/loadout';
import {
  HangarSchema,
  ItemPurchaseResultSchema,
  PurchaseResultSchema,
  SaveLoadoutResultSchema,
  type Hangar as HangarView,
  type ItemKind,
} from '@steelwake/shared/progression';
import { create } from 'zustand';

import { api, ApiError, describeError } from '../../api.js';
import { draftFor, sameLoadout } from './loadout.js';

export type HangarTab = 'loadout' | 'paint' | 'decals' | 'parts';

const credits = (n: number) => n.toLocaleString('en-US');

export function describeHangarError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'INSUFFICIENT_CREDITS') {
      const d = err.details as { price?: number; balance?: number } | undefined;
      if (typeof d?.price === 'number' && typeof d.balance === 'number') {
        return `You need ${credits(d.price - d.balance)} more credits for this.`;
      }
      return 'You do not have enough credits for this.';
    }
    if (err.code === 'ALREADY_OWNED') return 'You already own this.';
    if (err.code === 'PRICE_CHANGED') return 'The price changed just now; check it and try again.';
    if (err.code === 'ITEM_LOCKED' || err.code === 'INVALID_LOADOUT') {
      const issues = (err.details as { issues?: { message: string }[] } | undefined)?.issues;
      if (issues && issues.length > 0) return issues.map((i) => i.message).join('; ');
    }
  }
  return describeError(err);
}

export interface HangarState {
  view: HangarView | null;
  mech: string;
  tab: HangarTab;
  /** Edits per mech that differ from what was saved. */
  drafts: Record<string, Loadout>;
  busy: boolean;
  error: string | null;
  notice: string | null;
  /** The mech buy confirmation step is open. */
  confirming: boolean;
  setConfirming: (open: boolean) => void;
  load: () => Promise<void>;
  select: (mechId: string) => void;
  setTab: (tab: HangarTab) => void;
  edit: (update: (draft: Loadout, def: MechDefinition) => Loadout) => void;
  revert: () => void;
  save: () => Promise<void>;
  buyMech: () => Promise<void>;
  buyItem: (kind: ItemKind, id: string) => Promise<void>;
  clearMessages: () => void;
}

/** The draft shown for the selected mech: an edit in progress, or the saved loadout. */
export function selectDraft(s: Pick<HangarState, 'view' | 'mech' | 'drafts'>): Loadout {
  return s.drafts[s.mech] ?? draftFor(s.view, s.mech);
}

export function selectDirty(s: Pick<HangarState, 'view' | 'mech' | 'drafts'>): boolean {
  const draft = s.drafts[s.mech];
  return draft !== undefined && !sameLoadout(draft, draftFor(s.view, s.mech));
}

export const useHangarStore = create<HangarState>((set, get) => ({
  view: null,
  mech: 'warden',
  tab: 'loadout',
  drafts: {},
  busy: false,
  error: null,
  notice: null,
  confirming: false,

  setConfirming(open) {
    set({ confirming: open });
  },

  async load() {
    try {
      const view = HangarSchema.parse(await api<unknown>('/api/v1/hangar'));
      set({ view });
    } catch (err) {
      set({ error: describeHangarError(err) });
    }
  },

  select(mechId) {
    if (!MECHS[mechId]) return;
    set({ mech: mechId, error: null, notice: null, confirming: false });
  },

  setTab(tab) {
    set({ tab });
  },

  edit(update) {
    const s = get();
    const next = update(selectDraft(s), getMech(s.mech));
    set({ drafts: { ...s.drafts, [s.mech]: next }, notice: null, error: null });
  },

  revert() {
    const s = get();
    const { [s.mech]: _dropped, ...rest } = s.drafts;
    set({ drafts: rest, notice: null, error: null });
  },

  async save() {
    const s = get();
    const draft = selectDraft(s);
    set({ busy: true, error: null, notice: null });
    try {
      const result = SaveLoadoutResultSchema.parse(
        await api<unknown>(`/api/v1/hangar/loadouts/${s.mech}`, { method: 'POST', body: draft }),
      );
      const view = s.view
        ? { ...s.view, loadouts: { ...s.view.loadouts, [s.mech]: result.loadout } }
        : s.view;
      const { [s.mech]: _saved, ...rest } = get().drafts;
      set({ view, drafts: rest, notice: `${getMech(s.mech).name} loadout saved.` });
    } catch (err) {
      set({ error: describeHangarError(err) });
    } finally {
      set({ busy: false });
    }
  },

  async buyMech() {
    const s = get();
    const name = getMech(s.mech).name;
    set({ busy: true, error: null, notice: null });
    try {
      const shown = s.view?.mechs.find((m) => m.id === s.mech)?.price ?? null;
      const result = PurchaseResultSchema.parse(
        await api<unknown>(`/api/v1/hangar/mechs/${s.mech}/purchase`, {
          method: 'POST',
          ...(shown !== null ? { body: { expectedPrice: shown } } : {}),
        }),
      );
      set({
        notice: `${name} is yours. ${credits(result.balance)} credits left.`,
        confirming: false,
      });
    } catch (err) {
      set({ error: describeHangarError(err), confirming: false });
    } finally {
      set({ busy: false });
      await get().load();
    }
  },

  async buyItem(kind, id) {
    set({ busy: true, error: null, notice: null });
    try {
      const view = get().view;
      const shown =
        kind === 'module'
          ? (view?.modules.find((m) => m.id === id)?.price ?? null)
          : (view?.cosmetics.find((c) => c.id === id)?.price ?? null);
      const result = ItemPurchaseResultSchema.parse(
        await api<unknown>(`/api/v1/hangar/items/${kind}/${id}/purchase`, {
          method: 'POST',
          ...(shown !== null && shown > 0 ? { body: { expectedPrice: shown } } : {}),
        }),
      );
      set({
        notice: `Bought for ${credits(result.price)} credits; ${credits(result.balance)} left.`,
      });
    } catch (err) {
      set({ error: describeHangarError(err) });
    } finally {
      set({ busy: false });
      await get().load();
    }
  },

  clearMessages() {
    set({ error: null, notice: null });
  },
}));
